import { useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Input, SelectItem, TmdxSelect } from '@ds';
import { AddressSearch } from '@ui';
import { useLegFormContext } from '@lp/features/RoutePlanner/components/RouteLegForm/context/LegFormContext';
import { useCustomLocationAddress } from '@lp/features/RoutePlanner/components/RouteLegForm/hooks';
import { CommonRouteLegSchemaValues } from '@lp/features/RoutePlanner/components/RouteLegForm/validation';
import { resetLocationFields } from '@lp/features/RoutePlanner/components/RouteLegForm/utils';
import { useRouteLocations } from '@lp/features/RoutePlanner/hooks/useRouteLocations';
import { LegLocationTypeEnum, PredefinedPlaceType, RouteLocationType } from '@/api/apis/route';
import { CalculateGroundTimeType } from '@/api/apis/here-maps';
import { useWatchFields } from '@/hooks/useWatchFields';
import { SelectItemType } from '@/types';
import { getLocationAddress, renderSelectItem } from '@/utils/helpers';
import { useCountriesData } from '@/contexts/CountriesDataProvider';
import { useBaseCaseDetails } from '@/hooks/useBaseCaseDetails';
import { regionDefaultCountryById } from '@/constants/serviceRegion.constants';
import { DEFAULT_COUNTRY_ISO2 } from '@/constants/location.constants';

const PREDEFINED_LOCATION_TYPES = [
  LegLocationTypeEnum.hub,
  LegLocationTypeEnum.hospital,
  LegLocationTypeEnum.txcenter,
  LegLocationTypeEnum.homeResidence,
];

type SegmentLocationProps = {
  locationType: 'departureLocation' | 'arrivalLocation';
  predefinedLocations: PredefinedPlaceType[];
  countriesList: SelectItemType[];
  handleDateTime: (timezone: string) => void;
};

export const SegmentLocation = ({
  locationType,
  predefinedLocations,
  countriesList,
  handleDateTime,
}: SegmentLocationProps) => {
  const { resolveSubdivisionName } = useCountriesData();
  const { serviceRegionId } = useBaseCaseDetails();
  const { getLocationById } = useRouteLocations();
  const { isMainDataEdit, calculateLegTimeDuration, setLegDuration, runSubmitBlockingTransaction } =
    useLegFormContext();
  const { control, setValue, getValues } = useFormContext<CommonRouteLegSchemaValues>();
  const {
    [locationType]: locationValue,
    formType,
    legType,
  } = useWatchFields<CommonRouteLegSchemaValues>([locationType, 'departureDateTime', 'formType', 'legType'] as const);

  const [countrySearchValue, setCountrySearchValue] = useState('');

  const { addressList, onAddressSelect, onAddressInputChange } = useCustomLocationAddress({
    type: locationType,
    handleDateTime,
  });

  const resetCustomAddressFields = () => {
    setValue(`${locationType}.hereMapsId`, '');
    setValue(`${locationType}.displayName`, '');
    setValue(`${locationType}.id`, '');
    setValue(`${locationType}.longitude`, null);
    setValue(`${locationType}.latitude`, null);
    setValue(`${locationType}.timezone`, '');
  };

  const handleCountryChange = (countryCode: string) => {
    setValue(`${locationType}.country`, countryCode, { shouldValidate: true });
    resetCustomAddressFields();
    setCountrySearchValue('');
  };

  const placeTypeList = useMemo(() => {
    const defaultPlaces = [
      {
        key: LegLocationTypeEnum.airport,
        label: 'FBO',
      },
      {
        key: LegLocationTypeEnum.custom,
        label: 'Custom',
      },
    ];

    return [
      ...predefinedLocations.map((l) => ({
        key: `${l.type}|${l.id}`,
        label: l.label!,
      })),
      ...defaultPlaces,
    ];
  }, [predefinedLocations]);

  const filteredCountriesList = useMemo(() => {
    const search = countrySearchValue.trim().toLowerCase();

    if (!search) return countriesList;

    return countriesList.filter(
      (country) => country.name.toLowerCase().includes(search) || String(country.value).toLowerCase().includes(search),
    );
  }, [countriesList, countrySearchValue]);

  const [selectedLocationType, selectedLocationId] = locationValue.locationTypeId?.split('|') || [];
  const isPredefinedPlace = PREDEFINED_LOCATION_TYPES.includes(selectedLocationType as LegLocationTypeEnum);

  const currentLocation = useMemo<RouteLocationType | null>(() => {
    if (isPredefinedPlace) {
      const item = predefinedLocations.find((l) => l.id === selectedLocationId) || null;

      if (item) {
        return getLocationById(item.id) || null;
      }
    }

    return null;
  }, [getLocationById, isPredefinedPlace, predefinedLocations, selectedLocationId]);

  const address = useMemo(() => {
    if (!currentLocation || !currentLocation.location || locationValue.locationType === LegLocationTypeEnum.custom) {
      return '';
    }

    return getLocationAddress(currentLocation.location, resolveSubdivisionName);
  }, [currentLocation, locationValue.locationType, resolveSubdivisionName]);

  const handleLocationChange = async (locationTypeId: string) => {
    const [newLocationType, newLocationId] = locationTypeId?.split('|') || [];
    const isNewLocationPredefined = PREDEFINED_LOCATION_TYPES.includes(newLocationType as LegLocationTypeEnum);

    resetLocationFields(locationType, newLocationType as LegLocationTypeEnum, setValue);

    setValue(`${locationType}.locationType`, newLocationType as LegLocationTypeEnum);
    setValue(`${locationType}.locationTypeId`, locationTypeId);

    if (newLocationType === LegLocationTypeEnum.custom) {
      const defaultCountry =
        regionDefaultCountryById[serviceRegionId as keyof typeof regionDefaultCountryById] ?? DEFAULT_COUNTRY_ISO2;
      setValue(`${locationType}.country`, defaultCountry);
      setCountrySearchValue('');
    }

    if (isNewLocationPredefined) {
      const newLocation = predefinedLocations.find((l) => l.id === newLocationId) || null;

      if (!newLocation) return;

      const locationData = getLocationById(newLocation.id);

      if (!locationData) return;

      await runSubmitBlockingTransaction(async () => {
        setValue(`${locationType}.id`, newLocation.id);

        setValue(`${locationType}.longitude`, locationData.location.longitude);
        setValue(`${locationType}.latitude`, locationData.location.latitude);
        setValue(`${locationType}.timezone`, locationData.location.timezone);

        const timestamp = getValues('departureDateTime');

        const data = await calculateLegTimeDuration(legType, CalculateGroundTimeType.DepartureTime, timestamp);
        setLegDuration(formType, legType, data);
        handleDateTime(locationData.location.timezone);
      });
    }
  };

  return (
    <>
      <Controller
        name={`${locationType}.locationTypeId`}
        control={control}
        render={({ field: { value } }) => (
          <TmdxSelect
            placeholder="Choose an option"
            label="Select Type *"
            aria-label="Select Type"
            selectedKeys={value ? [value] : []}
            disallowEmptySelection
            isDisabled={!isMainDataEdit}
            onChange={(e) => {
              handleLocationChange(e.target.value);
            }}
          >
            {placeTypeList.map((e) => (
              <SelectItem key={e.key as string}>{e.label}</SelectItem>
            ))}
          </TmdxSelect>
        )}
      />

      {isPredefinedPlace && (
        <Controller
          name={`${locationType}.id`}
          control={control}
          render={({ fieldState: { invalid } }) => (
            <Input
              aria-label="Address"
              label="Address *"
              placeholder="-"
              value={address}
              isInvalid={invalid}
              isDisabled
            />
          )}
        />
      )}

      {locationValue.locationType === LegLocationTypeEnum.custom && (
        <>
          {countriesList.length > 1 && (
            <Controller
              name={`${locationType}.country`}
              control={control}
              render={({ field: { ref, value, ...field }, fieldState: { invalid } }) => (
                <TmdxSelect
                  placeholder="Choose an option"
                  aria-label="Country"
                  label="Country"
                  selectedKeys={value ? [value] : []}
                  isInvalid={invalid}
                  disallowEmptySelection
                  items={filteredCountriesList}
                  isDisabled={!isMainDataEdit}
                  onSearchValue={setCountrySearchValue}
                  searchValue={countrySearchValue}
                  renderValue={(items) => items.map((item) => item.key).join(', ')}
                  onOpenChange={(isOpen) => {
                    if (!isOpen) {
                      setCountrySearchValue('');
                    }
                  }}
                  onSelectionChange={(key) => {
                    handleCountryChange(key.currentKey as string);
                  }}
                  {...field}
                >
                  {renderSelectItem}
                </TmdxSelect>
              )}
            />
          )}

          <Controller
            name={`${locationType}.hereMapsId`}
            control={control}
            render={({ field: { ref, value, onChange, ...field }, fieldState: { invalid } }) => (
              <AddressSearch
                addressList={addressList}
                onAddressSelect={onAddressSelect}
                onAddressInputChange={onAddressInputChange}
                value={locationValue.displayName || ''}
                onChange={onChange}
                isInvalid={invalid}
                isDisabled={!isMainDataEdit}
                inputProps={{
                  placeholder: 'Search address',
                  label: 'Address *',
                  'aria-label': 'Address *',
                }}
                wrapperProps={{
                  'aria-label': 'Search Address',
                }}
                {...field}
              />
            )}
          />
        </>
      )}
    </>
  );
};
